import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, Alert, Spin, Button, Typography, Space } from "antd";
import { supabase } from "../lib/supabase";
import { useTranslation } from "react-i18next";
import {
  PackingSlipDocument,
  PackingSlipProduct,
} from "../components/PackingSlip";
import { pdf } from "@react-pdf/renderer";
import { DownloadOutlined, CopyOutlined } from "@ant-design/icons";

const { Text } = Typography;

// Define the debug info type to avoid 'any'
interface DebugInfo {
  bucketCreateError?: unknown;
  bucketCheckError?: unknown;
  authError?: unknown;
  urlParams?: Record<string, string>;
  missingParams?: string[];
  productArraysError?: {
    productIdsLen?: number;
    descriptionsLen?: number;
    piecesLen?: number;
    cartonsLen?: number;
    weightsLen?: number;
    cbmsLen?: number;
  };
  products?: PackingSlipProduct[];
  shipTo?: {
    company: string;
  };
  supplierInfo?: {
    id?: number;
    name?: string;
    email?: string;
    phone?: string;
    address?: string;
    country?: string;
    province?: string;
  };
  pdfSize?: number;
  base64Length?: number;
  uploadError?: unknown;
  urlError?: unknown;
  pdfError?: unknown;
}

// At the beginning of the file, add this helper function
function getRequiredParam(params: URLSearchParams, name: string): string {
  const value = params.get(name);
  if (value === null) {
    throw new Error(`Required parameter "${name}" is missing`);
  }
  return value;
}

export const RegeneratePackingSlipPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo>({});
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [copied, setCopied] = useState(false);

  // First, ensure the packingslips bucket exists
  useEffect(() => {
    const ensureBucketExists = async () => {
      try {
        console.log("Checking if packingslips bucket exists...");
        const { data: buckets, error: bucketCheckError } =
          await supabase.storage.listBuckets();

        if (bucketCheckError) {
          console.error("Error checking buckets:", bucketCheckError);
          setDebugInfo((prev) => ({ ...prev, bucketCheckError }));
          return;
        }

        const packingslipsBucket = buckets?.find(
          (bucket) => bucket.name === "packingslips"
        );

        if (!packingslipsBucket) {
          console.log("packingslips bucket not found, creating...");
          const { error: bucketCreateError } =
            await supabase.storage.createBucket("packingslips", {
              public: false,
            });

          if (bucketCreateError) {
            console.error("Error creating bucket:", bucketCreateError);
            setDebugInfo((prev) => ({ ...prev, bucketCreateError }));
          } else {
            console.log("packingslips bucket created successfully");
          }
        } else {
          console.log("packingslips bucket exists");
        }
      } catch (error) {
        console.error("Error in bucket check:", error);
      }
    };

    ensureBucketExists();
  }, []);

  useEffect(() => {
    const regeneratePackingSlip = async () => {
      try {
        console.log("Starting packing slip regeneration...");
        // Check authentication first
        const {
          data: { session },
          error: authError,
        } = await supabase.auth.getSession();

        if (authError) {
          console.error("Auth error:", authError);
          setDebugInfo((prev) => ({ ...prev, authError }));
          throw new Error(`Authentication error: ${authError.message}`);
        }

        if (!session) {
          console.log("No active session, redirecting to login");
          // Store the current URL in localStorage to redirect back after login
          const currentUrl = window.location.href;
          localStorage.setItem("redirectAfterAuth", currentUrl);
          setNeedsAuth(true);
          setLoading(false);
          return;
        }

        // Log all URL parameters for debugging
        console.log(
          "URL Parameters:",
          Object.fromEntries(searchParams.entries())
        );
        setDebugInfo((prev) => ({
          ...prev,
          urlParams: Object.fromEntries(searchParams.entries()),
        }));

        // Get all parameters from URL
        const date =
          searchParams.get("date") || new Date().toLocaleDateString("en-GB");
        const reference = searchParams.get("reference");
        const bookingId = searchParams.get("bookingId");
        const transportType = searchParams.get("transportType") || "";

        // Ship to info (now Customer)
        const shipToCompany = searchParams.get("shipToCompany");
        const shipToAddress = searchParams.get("shipToAddress");
        const shipToCity = searchParams.get("shipToCity");
        const shipToPostalCode = searchParams.get("shipToPostalCode");
        const shipToCountry = searchParams.get("shipToCountry");
        const shipToEoriNumber = searchParams.get("shipToEoriNumber");
        const shipToCocNumber = searchParams.get("shipToCocNumber");
        const shipToStreet = searchParams.get("shipToStreet");
        const shipToHouseNumber = searchParams.get("shipToHouseNumber");

        // Supplier info (optional)
        const supplierInfoId = searchParams.get("supplierInfoId");
        const supplierInfoName = searchParams.get("supplierInfoName");
        const supplierInfoEmail = searchParams.get("supplierInfoEmail");
        const supplierInfoPhone = searchParams.get("supplierInfoPhone");
        const supplierInfoAddress = searchParams.get("supplierInfoAddress");
        const supplierInfoCountry = searchParams.get("supplierInfoCountry");
        const supplierInfoProvince = searchParams.get("supplierInfoProvince");

        // Product info
        const companyId = searchParams.get("companyId");
        const productIdsParam = searchParams.get("productIds");
        const descriptionsParam = searchParams.get("descriptions");
        const piecesParam = searchParams.get("pieces");
        const cartonsParam = searchParams.get("cartons");
        const weightsParam = searchParams.get("weights");
        const netWeightsParam = searchParams.get("netWeights");
        const cbmsParam = searchParams.get("cbms");
        const eansParam = searchParams.get("eans");
        const hsCodesParam = searchParams.get("hsCodes");

        // Then, parse the net weights parameter like other array parameters
        const netWeights = netWeightsParam
          ? netWeightsParam.split(",").map(Number)
          : [];
        const hsCodes = hsCodesParam ? hsCodesParam.split(",") : [];

        // Check for missing parameters
        const missingParams: string[] = [];
        if (!reference) missingParams.push("reference");
        if (!bookingId) missingParams.push("bookingId");
        if (!shipToCompany) missingParams.push("shipToCompany");
        if (!companyId) missingParams.push("companyId");
        if (!productIdsParam) missingParams.push("productIds");
        if (!descriptionsParam) missingParams.push("descriptions");
        if (!piecesParam) missingParams.push("pieces");
        if (!cartonsParam) missingParams.push("cartons");
        if (!weightsParam) missingParams.push("weights");
        if (!cbmsParam) missingParams.push("cbms");

        if (missingParams.length > 0) {
          const errorMsg = `Missing required parameters: ${missingParams.join(
            ", "
          )}`;
          console.error(errorMsg);
          setDebugInfo((prev) => ({ ...prev, missingParams }));
          throw new Error(errorMsg);
        }

        // After validation, get parameters using our helper function
        const productIds = getRequiredParam(searchParams, "productIds")
          .split(",")
          .map(Number);
        const descriptions = getRequiredParam(
          searchParams,
          "descriptions"
        ).split(",");
        const pieces = getRequiredParam(searchParams, "pieces")
          .split(",")
          .map(Number);
        const cartons = getRequiredParam(searchParams, "cartons")
          .split(",")
          .map(Number);
        const weights = getRequiredParam(searchParams, "weights")
          .split(",")
          .map(Number);
        const cbms = getRequiredParam(searchParams, "cbms")
          .split(",")
          .map(Number);
        // This one might be null since we don't check it in missingParams
        const eans = eansParam ? eansParam.split(",") : [];

        const productCount = productIds.length;
        console.log(`Found ${productCount} products`);
        console.log(`EANs provided: ${eans.length > 0 ? "Yes" : "No"}`);

        // Check if all arrays have the same length
        if (
          descriptions?.length !== productCount ||
          pieces?.length !== productCount ||
          cartons?.length !== productCount ||
          weights?.length !== productCount ||
          cbms?.length !== productCount
        ) {
          const errorMsg = "Product arrays have inconsistent lengths";
          console.error(errorMsg, {
            productIdsLen: productIds?.length,
            descriptionsLen: descriptions?.length,
            piecesLen: pieces?.length,
            cartonsLen: cartons?.length,
            weightsLen: weights?.length,
            cbmsLen: cbms?.length,
          });
          setDebugInfo((prev) => ({
            ...prev,
            productArraysError: {
              productIdsLen: productIds?.length,
              descriptionsLen: descriptions?.length,
              piecesLen: pieces?.length,
              cartonsLen: cartons?.length,
              weightsLen: weights?.length,
              cbmsLen: cbms?.length,
            },
          }));
          throw new Error(errorMsg);
        }

        // Create products array
        const products: PackingSlipProduct[] = [];
        for (let i = 0; i < productCount; i++) {
          // Generate a default EAN from the product ID if none provided
          // Format the product ID as a 13-digit string with leading zeros
          const defaultEan = String(productIds[i]).padStart(13, "0");

          products.push({
            id: productIds[i],
            ean: eans[i] || defaultEan, // Use default EAN if none provided
            description: descriptions[i],
            pieces: pieces[i],
            cartons: cartons[i],
            weight: weights[i],
            net_weight: netWeights[i] || undefined,
            cbm: cbms[i],
            hsCode: hsCodes[i] || undefined,
          });
        }

        console.log("Products data:", products);
        setDebugInfo((prev) => ({ ...prev, products }));

        // Create shipTo object
        const shipTo = {
          company: shipToCompany || "Unknown Company",
          address: shipToAddress || "",
          city: shipToCity || "",
          postalCode: shipToPostalCode || "",
          country: shipToCountry || "",
          eori_number: shipToEoriNumber || "",
          coc_number: shipToCocNumber || "",
          street: shipToStreet || "",
          house_number: shipToHouseNumber || "",
        };

        // Create supplierInfo object if any supplier data provided
        let supplierInfo = undefined;
        if (supplierInfoName || supplierInfoAddress || supplierInfoCountry) {
          supplierInfo = {
            id: supplierInfoId ? parseInt(supplierInfoId, 10) : undefined,
            name: supplierInfoName || "",
            email: supplierInfoEmail || "",
            phone: supplierInfoPhone || "",
            address: supplierInfoAddress || "",
            country: supplierInfoCountry || "",
            province: supplierInfoProvince || "",
          };
        }

        console.log("Ship to:", shipTo);
        console.log("Supplier info:", supplierInfo);
        setDebugInfo((prev) => ({ ...prev, shipTo, supplierInfo }));

        // Generate the PDF blob
        console.log("Generating PDF...");
        try {
          console.log("Creating document with:", {
            date,
            reference: reference || "UNKNOWN", // Ensure reference is never null
            shipTo,
            supplierInfo,
            products: products.length,
          });

          // Create a minimal version of the document for testing
          const simplifiedProducts = products.map((p) => ({
            ...p,
            description: p.description.substring(0, 50), // Limit description length for safety
          }));

          const blob = await pdf(
            PackingSlipDocument({
              date,
              reference: reference || "UNKNOWN", // Ensure reference is never null
              shipTo,
              supplierInfo,
              products: simplifiedProducts,
            })
          ).toBlob();

          console.log("PDF generated successfully, size:", blob.size);
          setDebugInfo((prev) => ({ ...prev, pdfSize: blob.size }));

          // Convert blob to base64 for storage
          console.log("Converting PDF to base64...");
          const reader = new FileReader();
          const base64Promise = new Promise<string>((resolve, reject) => {
            reader.onloadend = () => {
              try {
                const base64data = reader.result as string;
                resolve(base64data.split(",")[1]); // Remove the data URL prefix
              } catch (e) {
                reject(e);
              }
            };
            reader.onerror = (e) => reject(e);
          });
          reader.readAsDataURL(blob);
          const base64Data = await base64Promise;

          console.log("PDF converted to base64, length:", base64Data.length);
          setDebugInfo((prev) => ({
            ...prev,
            base64Length: base64Data.length,
          }));

          // Save to Supabase
          const filePath = `${companyId}/${bookingId}/packing_slip_${bookingId}_${transportType}.pdf`;
          console.log("Uploading to path:", filePath);

          const decodedData = decode(base64Data);
          console.log("Decoded base64 size:", decodedData.length);

          console.log("Uploading to Supabase storage...");
          const { error: uploadError } = await supabase.storage
            .from("packingslips")
            .upload(filePath, decodedData, {
              contentType: "application/pdf",
              upsert: true,
            });

          if (uploadError) {
            console.error("Upload error:", uploadError);
            setDebugInfo((prev) => ({ ...prev, uploadError }));
            throw new Error(`Upload error: ${uploadError.message}`);
          }

          console.log("File uploaded successfully, generating signed URL...");
          // Get the download URL
          const { data: urlData, error: urlError } = await supabase.storage
            .from("packingslips")
            .createSignedUrl(filePath, 3600);

          if (urlError) {
            console.error("Signed URL error:", urlError);
            setDebugInfo((prev) => ({ ...prev, urlError }));
            throw new Error(`Failed to get download URL: ${urlError.message}`);
          }

          if (urlData?.signedUrl) {
            console.log("Signed URL generated successfully");
            // Set the URL and filename for display
            setSignedUrl(urlData.signedUrl);
            setFileName(`packing_slip_${bookingId}_${transportType}.pdf`);
          } else {
            throw new Error("No signed URL returned");
          }
        } catch (pdfError) {
          console.error("PDF generation/upload error:", pdfError);
          setDebugInfo((prev) => ({ ...prev, pdfError }));
          throw new Error(
            `Error generating or uploading PDF: ${
              pdfError instanceof Error ? pdfError.message : String(pdfError)
            }`
          );
        }
      } catch (err) {
        console.error("Error regenerating packing slip:", err);
        setError(
          err instanceof Error ? err.message : t("packingSlip.regenerateError")
        );
      } finally {
        setLoading(false);
      }
    };

    regeneratePackingSlip();
  }, [searchParams, t, navigate]);

  const handleCopyUrl = () => {
    if (signedUrl) {
      navigator.clipboard.writeText(signedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (needsAuth) {
    return (
      <Card style={{ maxWidth: 600, margin: "50px auto", textAlign: "center" }}>
        <Alert
          message={t("packingSlip.authRequired")}
          type="info"
          showIcon
          style={{ marginBottom: "20px" }}
        />
        <Button type="primary" onClick={() => navigate("/auth")}>
          {t("common.login")}
        </Button>
      </Card>
    );
  }

  if (!loading && error) {
    return (
      <Card style={{ maxWidth: 800, margin: "50px auto" }}>
        <Alert
          message="Error Generating Packing Slip"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: "20px" }}
        />
        <div>
          <p>Try these troubleshooting steps:</p>
          <ol>
            <li>Make sure you're logged in</li>
            <li>Check that all parameters are correctly formatted</li>
            <li>Try refreshing the page</li>
            <li>Contact support if the issue persists</li>
          </ol>
        </div>
        {process.env.NODE_ENV === "development" && (
          <div
            style={{
              marginTop: 20,
              overflow: "auto",
              maxHeight: 400,
              border: "1px solid #ddd",
              padding: 10,
            }}
          >
            <h4>Debug Information:</h4>
            <pre style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(debugInfo, null, 2)}
            </pre>
          </div>
        )}
      </Card>
    );
  }

  if (!loading && signedUrl) {
    return (
      <Card style={{ maxWidth: 600, margin: "50px auto", textAlign: "center" }}>
        <Alert
          message={t("packingSlip.generatedSuccess")}
          type="success"
          showIcon
          style={{ marginBottom: "20px" }}
        />
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Text>{t("packingSlip.downloadDescription")}</Text>
          <Space>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              href={signedUrl}
              target="_blank"
              download={fileName}
            >
              {t("packingSlip.downloadSlip")}
            </Button>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyUrl}
              type={copied ? "primary" : "default"}
            >
              {copied ? t("common.copied") : t("common.copyLink")}
            </Button>
          </Space>
          <Text type="secondary">{t("packingSlip.linkExpiryWarning")}</Text>
        </Space>
      </Card>
    );
  }

  return (
    <div style={{ textAlign: "center", padding: "50px" }}>
      <Spin size="large" />
      <p style={{ marginTop: "20px" }}>{t("packingSlip.regeneratingSlip")}</p>
    </div>
  );
};

// Helper function to decode base64
function decode(base64: string): Uint8Array {
  try {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  } catch (e) {
    console.error("Decode error:", e);
    throw new Error(
      `Failed to decode base64: ${e instanceof Error ? e.message : String(e)}`
    );
  }
}
